import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc,
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  limit,
  startAfter,
  serverTimestamp,
  deleteField,
  onSnapshot 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Atendimento, AtendimentoFormData, ApiResponse } from '../types';

export const subscribeToAtendimentosDoDia = (
  userId: string,
  data: string,
  callback: (atendimentos: Atendimento[]) => void,
  onError?: (error: Error) => void
): (() => void) => {
  try {
    const dataAtendimento = data || new Date().toISOString().split('T')[0];
    
    const q = query(
      collection(db, 'atendimentos'),
      where('userId', '==', userId),
      where('dataAtendimento', '==', dataAtendimento)
    );
    
    // onSnapshot retorna uma função para desinscrever o listener
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const atendimentos: Atendimento[] = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate()
        } as Atendimento));

        // Ordenar por hora de início
        atendimentos.sort((a, b) => {
          return a.horaInicio.localeCompare(b.horaInicio);
        });

        // Chamar callback com os dados atualizados
        callback(atendimentos);
      },
      (error) => {
        console.error('❌ Erro no listener de atendimentos:', error);
        if (onError) {
          onError(error);
        }
      }
    );

    return unsubscribe;
  } catch (error: any) {
    console.error('❌ Erro ao criar listener:', error);
    if (onError) {
      onError(error);
    }
    return () => {}; // Retorna função vazia em caso de erro
  }
};

// Gerar número único para atendimento usando timestamp + random
// VERSÃO 2.0 - SEM CONSULTA AO FIRESTORE
const generateUniqueAtendimentoNumber = (): string => {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  
  // Usar timestamp para garantir unicidade (últimos 4 dígitos do timestamp)
  const timestamp = now.getTime();
  const uniqueNum = timestamp.toString().slice(-4);
  
  // Adicionar 2 dígitos aleatórios extras para garantir ainda mais unicidade
  const randomExtra = Math.floor(Math.random() * 100).toString().padStart(2, '0');
  
  return `AT-${day}${month}-${uniqueNum}${randomExtra}`;
};

// Criar atendimento
// VERSÃO 2.0 - SEM VERIFICAÇÃO DE DUPLICAÇÃO
export const createAtendimento = async (
  atendimentoData: AtendimentoFormData, 
  funcionarioNome: string,
  userId: string
): Promise<ApiResponse<Atendimento>> => {
  try {
    const numeroAtendimento = generateUniqueAtendimentoNumber();
    const now = new Date();
    const dataAtendimento = now.toISOString().split('T')[0]; // YYYY-MM-DD
    const horaInicio = now.toTimeString().split(' ')[0]; // HH:MM:SS
    
    // Construir objeto base sem campos undefined (Firebase não aceita undefined)
    const atendimentoToSave: any = {
      numeroAtendimento,
      clienteNome: atendimentoData.clienteNome,
      funcionarioId: atendimentoData.funcionarioId,
      funcionarioNome,
      tipoAtendimento: atendimentoData.tipoAtendimento,
      dataAtendimento,
      horaInicio,
      status: 'em_andamento' as const,
      observacoes: atendimentoData.observacoes || '',
      userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Adicionar clienteId apenas se existir (para evitar undefined no Firebase)
    if (atendimentoData.clienteId) {
      atendimentoToSave.clienteId = atendimentoData.clienteId;
    }

    const docRef = await addDoc(collection(db, 'atendimentos'), atendimentoToSave);

    const atendimento: Atendimento = {
      id: docRef.id,
      numeroAtendimento,
      clienteNome: atendimentoData.clienteNome,
      clienteId: atendimentoData.clienteId,
      funcionarioId: atendimentoData.funcionarioId,
      funcionarioNome,
      tipoAtendimento: atendimentoData.tipoAtendimento as Atendimento['tipoAtendimento'],
      dataAtendimento,
      horaInicio,
      status: 'em_andamento',
      observacoes: atendimentoData.observacoes || '',
      userId,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    return {
      success: true,
      data: atendimento,
      id: docRef.id
    };
  } catch (error: any) {
    console.error('❌ Erro ao criar atendimento:', error);
    console.error('❌ Detalhes do erro:', {
      code: error.code,
      message: error.message,
      name: error.name
    });
    
    let errorMessage = 'Erro ao criar atendimento';
    
    if (error.code === 'permission-denied') {
      errorMessage = 'Sem permissão para criar atendimento. Verifique suas credenciais.';
    } else if (error.message) {
      errorMessage = `Erro: ${error.message}`;
    }
    
    return {
      success: false,
      error: errorMessage
    };
  }
};

export const addProdutosAtendimento = async (
  id: string, 
  data: {
    observacoes: string;
    produtosSelecionados: Array<{
      id: string;
      titulo: string;
      tamanho: string;
      cor: string;
      codigoProduto: string;
      observacao?: string;
    }>;
  }
): Promise<ApiResponse<void>> => {
  try {
    const atendimentoRef = doc(db, 'atendimentos', id);
    
    const updateData = {
      observacoes: data.observacoes,
      produtosSelecionados: data.produtosSelecionados,
      updatedAt: serverTimestamp()
    };

    await updateDoc(atendimentoRef, updateData);
    
    return { success: true };
  } catch (error: any) {
    console.error('Erro ao atualizar atendimento:', error);
    return { 
      success: false, 
      error: `Erro ao atualizar atendimento: ${error.message}` 
    };
  }
};

// Buscar atendimentos
export const getAtendimentos = async (userId: string): Promise<ApiResponse<Atendimento[]>> => {
  try {
    if (!userId) {
      return {
        success: false,
        error: 'Usuário não autenticado'
      };
    }

    let q = query(
      collection(db, 'atendimentos'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    
    try {
      const querySnapshot = await getDocs(q);
      const atendimentos: Atendimento[] = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      } as Atendimento));

      return {
        success: true,
        data: atendimentos
      };
      
    } catch (orderByError) {
      // Se orderBy falhar, tentar query simples sem ordenação
      q = query(
        collection(db, 'atendimentos'),
        where('userId', '==', userId)
      );
      
      const querySnapshot = await getDocs(q);
      const atendimentos: Atendimento[] = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      } as Atendimento));

      // Ordenar no JavaScript (mais recente primeiro)
      atendimentos.sort((a, b) => {
        const dateA = a.createdAt || new Date(0);
        const dateB = b.createdAt || new Date(0);
        return dateB.getTime() - dateA.getTime();
      });

      return {
        success: true,
        data: atendimentos
      };
    }
    
  } catch (error: any) {
    console.error('Erro ao buscar atendimentos:', error);
    return {
      success: false,
      error: `Erro ao buscar atendimentos: ${error.message}`
    };
  }
};

// Finalizar atendimento
export const finalizarAtendimento = async (
  id: string, 
  resultado: string, 
  observacoes?: string,
  reservaId?: string
): Promise<ApiResponse<void>> => {
  try {
    const atendimentoRef = doc(db, 'atendimentos', id);
    const now = new Date();
    const horaFim = now.toTimeString().split(' ')[0];
    
    // Buscar atendimento para calcular duração
    const atendimentoSnap = await getDoc(atendimentoRef);
    
    let duracaoMinutos = 0;
    if (atendimentoSnap.exists()) {
      const atendimento = atendimentoSnap.data();
      if (atendimento?.horaInicio) {
        const [horaI, minI, segI] = atendimento.horaInicio.split(':').map(Number);
        const [horaF, minF, segF] = horaFim.split(':').map(Number);
        const inicioMinutos = horaI * 60 + minI;
        const fimMinutos = horaF * 60 + minF;
        duracaoMinutos = fimMinutos - inicioMinutos;
      }
    }

    const updates: any = {
      status: 'finalizado',
      horaFim,
      duracaoMinutos,
      resultado,
      updatedAt: serverTimestamp()
    };

    if (observacoes !== undefined) {
      updates.observacoes = observacoes;
    }

    if (reservaId) {
      updates.reservaId = reservaId;
    }

    await updateDoc(atendimentoRef, updates);

    return {
      success: true
    };
  } catch (error) {
    console.error('Erro ao finalizar atendimento:', error);
    return {
      success: false,
      error: 'Erro ao finalizar atendimento'
    };
  }
};

// Cancelar atendimento
export const cancelarAtendimento = async (id: string, motivo?: string): Promise<ApiResponse<void>> => {
  try {
    const atendimentoRef = doc(db, 'atendimentos', id);
    await updateDoc(atendimentoRef, {
      status: 'cancelado',
      observacoes: motivo || 'Atendimento cancelado',
      updatedAt: serverTimestamp()
    });

    return {
      success: true
    };
  } catch (error) {
    console.error('Erro ao cancelar atendimento:', error);
    return {
      success: false,
      error: 'Erro ao cancelar atendimento'
    };
  }
};

// Atualizar atendimento
export const updateAtendimento = async (id: string, updates: Partial<Atendimento>): Promise<ApiResponse<void>> => {
  try {
    const atendimentoRef = doc(db, 'atendimentos', id);
    
    // Preparar dados para atualização
    const updateData: any = {
      ...updates,
      updatedAt: serverTimestamp()
    };

    // Se clienteId é null, usar deleteField para remover o campo
    if (updates.clienteId === null) {
      updateData.clienteId = deleteField();
    }

    await updateDoc(atendimentoRef, updateData);

    return {
      success: true
    };
  } catch (error: any) {
    console.error('Erro ao atualizar atendimento:', error);
    return {
      success: false,
      error: `Erro ao atualizar atendimento: ${error.message || 'Erro desconhecido'}`
    };
  }
};

// Deletar atendimento
export const deleteAtendimento = async (id: string): Promise<ApiResponse<void>> => {
  try {
    await deleteDoc(doc(db, 'atendimentos', id));
    return {
      success: true
    };
  } catch (error) {
    console.error('Erro ao deletar atendimento:', error);
    return {
      success: false,
      error: 'Erro ao deletar atendimento'
    };
  }
};

// Buscar atendimentos do dia
export const getAtendimentosDoDia = async (userId: string, data?: string): Promise<ApiResponse<Atendimento[]>> => {
  try {
    const dataAtendimento = data || new Date().toISOString().split('T')[0];
    
    const q = query(
      collection(db, 'atendimentos'),
      where('userId', '==', userId),
      where('dataAtendimento', '==', dataAtendimento)
    );
    
    const querySnapshot = await getDocs(q);
    const atendimentos: Atendimento[] = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate()
    } as Atendimento));

    // Ordenar por hora de início
    atendimentos.sort((a, b) => {
      return a.horaInicio.localeCompare(b.horaInicio);
    });

    return {
      success: true,
      data: atendimentos
    };
  } catch (error: any) {
    console.error('Erro ao buscar atendimentos do dia:', error);
    return {
      success: false,
      error: `Erro ao buscar atendimentos do dia: ${error.message}`
    };
  }
};

// Buscar atendimentos com paginação (para histórico)
export const getAtendimentosPaginated = async (
  userId: string, 
  pageSize: number = 20, 
  lastDoc?: any
): Promise<ApiResponse<{ items: Atendimento[], lastDoc: any, hasMore: boolean }>> => {
  try {
    if (!userId) {
      return {
        success: false,
        error: 'Usuário não autenticado'
      };
    }

    // Busca pageSize + 1 para saber se há mais páginas
    const pageLimit = pageSize + 1;

    let q = query(
      collection(db, 'atendimentos'),
      where('userId', '==', userId),
      orderBy('dataAtendimento', 'desc'),
      orderBy('horaInicio', 'desc'),
      limit(pageLimit)
    );

    // Se tem lastDoc, busca a partir dele
    if (lastDoc) {
      q = query(
        collection(db, 'atendimentos'),
        where('userId', '==', userId),
        orderBy('dataAtendimento', 'desc'),
        orderBy('horaInicio', 'desc'),
        startAfter(lastDoc),
        limit(pageLimit)
      );
    }

    const querySnapshot = await getDocs(q);
    const docs = querySnapshot.docs;
    
    // Verifica se tem mais páginas
    const hasMore = docs.length > pageSize;
    
    // Remove o documento extra se houver
    const items = docs.slice(0, pageSize).map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate()
    } as Atendimento));

    return {
      success: true,
      data: {
        items,
        lastDoc: hasMore ? docs[pageSize - 1] : null,
        hasMore
      }
    };
  } catch (error: any) {
    console.error('Erro ao buscar atendimentos paginados:', error);
    
    // Se falhar com orderBy (índice não criado), tenta sem
    try {
      let q = query(
        collection(db, 'atendimentos'),
        where('userId', '==', userId),
        limit(pageSize + 1)
      );

      if (lastDoc) {
        q = query(
          collection(db, 'atendimentos'),
          where('userId', '==', userId),
          startAfter(lastDoc),
          limit(pageSize + 1)
        );
      }

      const querySnapshot = await getDocs(q);
      const docs = querySnapshot.docs;
      const hasMore = docs.length > pageSize;
      
      const items = docs.slice(0, pageSize).map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      } as Atendimento));

      // Ordenar no JavaScript
      items.sort((a, b) => {
        const dateCompare = b.dataAtendimento.localeCompare(a.dataAtendimento);
        if (dateCompare !== 0) return dateCompare;
        return b.horaInicio.localeCompare(a.horaInicio);
      });

      return {
        success: true,
        data: {
          items,
          lastDoc: hasMore ? docs[pageSize - 1] : null,
          hasMore
        }
      };
    } catch (fallbackError: any) {
      return {
        success: false,
        error: `Erro ao buscar atendimentos: ${fallbackError.message}`
      };
    }
  }
};

